/**
 * Collection Load More / 下拉加载
 * Automatically loads next page products when scrolling to the bottom.
 * Also supports manual "Load More" button click.
 */
class CollectionLoadMore extends HTMLElement {
  constructor() {
    super();
    this.loadMoreBtn = this.querySelector('[data-load-more-btn]');
    this.loadingEl = this.querySelector('[data-load-more-loading]');
    this.endEl = this.querySelector('[data-load-more-end]');
    this.productGrid = document.querySelector('.grid--uniform.small--grid--flush');

    if (!this.loadMoreBtn || !this.productGrid) return;

    this.isLoading = false;
    this.baseUrl = this.loadMoreBtn.getAttribute('data-next-url') || '';

    this.loadMoreBtn.addEventListener('click', () => this.loadMore());
    this.initIntersectionObserver();
  }

  initIntersectionObserver() {
    if (!('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.isLoading) {
            const btnVisible = this.loadMoreBtn.style.display !== 'none';
            if (btnVisible) {
              this.loadMore();
            }
          }
        });
      },
      {
        rootMargin: '300px 0px',
        threshold: 0
      }
    );
    // Observe the wrapper element itself (always in DOM) rather than the button (may hide)
    this.observer.observe(this);
  }

  async loadMore() {
    if (this.isLoading) return;

    const nextUrl = this.loadMoreBtn.getAttribute('data-next-url');
    if (!nextUrl) return;

    this.isLoading = true;
    this.showLoading();

    try {
      const response = await fetch(nextUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Extract product cards from the fetched page
      const newProducts = doc.querySelectorAll('.grid--uniform.small--grid--flush .grid__item.grid-product');

      if (newProducts.length === 0) {
        this.showEnd();
        return;
      }

      // Append each product to the existing grid
      newProducts.forEach((product) => {
        // Initialize AOS animation attributes
        product.setAttribute('data-aos', 'row-of-4');
        this.productGrid.appendChild(product);
      });

      // Refresh AOS if available
      if (typeof AOS !== 'undefined' && AOS.refresh) {
        AOS.refresh();
      }

      // Update pagination state
      this.updatePaginationState();

      // Re-initialize Loox reviews if available
      if (typeof loox !== 'undefined' && loox && typeof loox.visit === 'function') {
        loox.visit();
      }

    } catch (error) {
      console.error('Collection load more failed:', error);
      this.showLoadMoreBtn();
    } finally {
      this.isLoading = false;
      this.hideLoading();
    }
  }

  updatePaginationState() {
    const currentPage = parseInt(this.loadMoreBtn.getAttribute('data-current-page')) || 1;
    const totalPages = parseInt(this.loadMoreBtn.getAttribute('data-total-pages')) || 1;
    const nextPage = currentPage + 1;

    if (nextPage > totalPages) {
      this.showEnd();
      return;
    }

    // Update current page attribute
    this.loadMoreBtn.setAttribute('data-current-page', nextPage);

    // Build next page URL by updating the page parameter
    if (nextPage < totalPages) {
      const nextUrl = this.buildPageUrl(nextPage + 1);
      this.loadMoreBtn.setAttribute('data-next-url', nextUrl);
    } else {
      this.loadMoreBtn.removeAttribute('data-next-url');
    }
  }

  buildPageUrl(pageNum) {
    // Replace or add page parameter in the URL
    let url = this.baseUrl;
    if (url.includes('page=')) {
      url = url.replace(/page=\d+/, `page=${pageNum}`);
    } else {
      const separator = url.includes('?') ? '&' : '?';
      url = `${url}${separator}page=${pageNum}`;
    }
    return url;
  }

  showLoading() {
    if (this.loadMoreBtn) this.loadMoreBtn.style.display = 'none';
    if (this.loadingEl) this.loadingEl.style.display = 'flex';
    if (this.endEl) this.endEl.style.display = 'none';
  }

  hideLoading() {
    if (this.loadingEl) this.loadingEl.style.display = 'none';
    if (this.loadMoreBtn) this.loadMoreBtn.style.display = 'inline-block';
  }

  showLoadMoreBtn() {
    if (this.loadingEl) this.loadingEl.style.display = 'none';
    if (this.endEl) this.endEl.style.display = 'none';
    if (this.loadMoreBtn) this.loadMoreBtn.style.display = 'inline-block';
  }

  showEnd() {
    if (this.loadMoreBtn) this.loadMoreBtn.style.display = 'none';
    if (this.loadingEl) this.loadingEl.style.display = 'none';
    if (this.endEl) this.endEl.style.display = 'block';
    // Disconnect observer when all products are loaded
    if (this.observer) this.observer.disconnect();
  }
}

customElements.define('collection-load-more', CollectionLoadMore);
